import React from 'react';
import { Customer } from '../../types';
import { DuplicateHit } from '../../services/customerDuplicates';
import { Button } from '../Button';
import { AlertTriangle, ArchiveRestore, Building2, ChevronRight, Plus, X } from 'lucide-react';

/**
 * "Aehnlicher Kunde vorhanden" — gemeinsamer Dialog fuer Neuanlage und
 * Import. Er verhindert die zweite Kundennummer fuer dieselbe Firma:
 * Nummern werden nie zusammengelegt, deshalb ist die richtige Entscheidung
 * VOR dem Anlegen die einzige billige.
 */
interface Props {
    hits: DuplicateHit[];
    /** Name des Kandidaten, der angelegt werden sollte — nur Anzeige. */
    candidateName: string;
    onOpenExisting: (c: Customer) => void;
    /** Beschriftung des Knopfs fuer onOpenExisting (Angebot: "Übernehmen"). */
    openLabel?: string;
    /** Archivierte Treffer ueber onOpenExisting anbieten? Sonst nur Wiederherstellen. */
    openArchived?: boolean;
    onRestore?: (c: Customer) => void;
    onCreateAnyway: () => void;
    onClose: () => void;
}

export const DuplicateCustomerDialog: React.FC<Props> = ({
    hits,
    candidateName,
    onOpenExisting,
    openLabel = 'Öffnen',
    openArchived = true,
    onRestore,
    onCreateAnyway,
    onClose,
}) => (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in">
            <div className="bg-amber-600 text-white p-5 flex justify-between items-center">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Ähnlicher Kunde vorhanden
                </h3>
                <button onClick={onClose} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[50vh] overflow-y-auto">
                <p className="text-sm text-slate-600">
                    Für <strong>{candidateName}</strong> gibt es bereits ähnliche Einträge.
                    Ein zweiter Kunde bekäme eine <strong>neue Kundennummer</strong> — Nummern
                    lassen sich später nicht zusammenlegen.
                </p>
                {hits.map((h) => (
                    <div key={h.customer.id} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-800 truncate">{h.customer.name}</span>
                                {h.customer.customerNumber && (
                                    <span className="text-[11px] text-slate-400 font-mono">{h.customer.customerNumber}</span>
                                )}
                                {h.customer.archivedAt && (
                                    <span className="text-[11px] font-bold uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">Archiviert</span>
                                )}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {[h.customer.zip, h.customer.city].filter(Boolean).join(' ')} · {h.reasons.join(', ')}
                            </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                            {h.customer.archivedAt && !h.customer.mergedIntoId && onRestore && (
                                <Button variant="secondary" onClick={() => onRestore(h.customer)} icon={<ArchiveRestore className="w-4 h-4" />}>
                                    Wiederherstellen
                                </Button>
                            )}
                            {(openArchived || !h.customer.archivedAt) && (
                                <Button variant="secondary" onClick={() => onOpenExisting(h.customer)} icon={<ChevronRight className="w-4 h-4" />}>
                                    {openLabel}
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="p-4 bg-slate-50 border-t flex justify-end gap-3">
                <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
                <Button variant="primary" onClick={onCreateAnyway} icon={<Plus className="w-4 h-4" />}>
                    Trotzdem neu anlegen
                </Button>
            </div>
        </div>
    </div>
);
